import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Field } from "@/components/common";
import { adminApi, brl, errMsg, MODULE_LABELS } from "@/lib/api";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const EMPTY = { name: "", description: "", max_users: 0, max_products: 0, max_orders: 0, max_stores: 1, modules: Object.keys(MODULE_LABELS), module_prices: {}, yearly_discount: 15, support: "E-mail", status: "active", highlight: false, order: 0, features: [], custom: false };
const NUMS = ["yearly_discount", "max_users", "max_products", "max_orders", "max_stores", "order"];
const cents = (v) => Math.round((Number(v) || 0) * 100);

const planTotals = (f) => {
  const monthly = f.modules.reduce((s, m) => s + cents(f.module_prices[m]), 0) / 100;
  return { monthly, yearly: Math.round(monthly * 12 * (1 - (Number(f.yearly_discount) || 0) / 100) * 100) / 100 };
};

function ModulePrices({ f, modules, toggleMod, setPrice }) {
  const Toggle = f.custom ? Switch : Checkbox;
  return (
    <div className="sm:col-span-2">
      <p className="text-sm font-medium">{f.custom ? "Módulos disponíveis para o cliente escolher" : "Módulos disponíveis"}</p>
      <p className="text-xs text-muted-foreground mb-2">{f.custom
        ? "Ligue os módulos que o cliente pode escolher e coloque o valor mensal de cada um. O cliente paga a soma dos que escolher. Módulo ligado sem valor sai de graça."
        : "Marque os módulos do plano e coloque o valor mensal de cada um. Só os marcados entram na soma."}</p>
      <div className="grid sm:grid-cols-2 gap-2" data-testid="plan-modules">
        {modules.map((m) => {
          const on = f.modules.includes(m);
          return (
            <div key={m} className={cn("flex items-center gap-2 rounded-md border px-3 py-1.5 transition-opacity", !on && "opacity-50")}>
              <label className="flex flex-1 min-w-0 items-center gap-2 text-sm"><Toggle checked={on} onCheckedChange={() => toggleMod(m)} data-testid={`plan-module-${m}`} /><span className="truncate">{MODULE_LABELS[m] || m}</span></label>
              <span className="text-xs text-muted-foreground">R$</span>
              <Input type="number" step="0.01" min="0" value={f.module_prices[m] ?? ""} onChange={(e) => setPrice(m, e.target.value)} className="h-8 w-24 text-right font-mono" aria-label={`Valor do módulo ${MODULE_LABELS[m] || m}`} data-testid={`plan-module-price-${m}`} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PriceSummary({ f, set }) {
  const { monthly, yearly } = planTotals(f);
  return (
    <div className="sm:col-span-2 grid sm:grid-cols-3 gap-4 rounded-lg border bg-muted/40 p-4" data-testid="plan-price-summary">
      <div><p className="text-xs text-muted-foreground">{f.custom ? "Valor com todos os módulos ligados" : "Valor mensal (soma dos módulos)"}</p><p className="font-display text-2xl font-bold" data-testid="plan-monthly-total">{brl(monthly)}</p></div>
      <Field label="Desconto no anual 12x (%)"><Input type="number" step="0.01" min="0" max="100" value={f.yearly_discount} onChange={set("yearly_discount")} data-testid="plan-yearly-discount-input" /></Field>
      <div><p className="text-xs text-muted-foreground">Valor anual (12x com desconto)</p><p className="font-display text-2xl font-bold" data-testid="plan-yearly-total">{brl(yearly)}</p>
        <p className="text-xs text-muted-foreground">equivale a {brl(yearly / 12)}/mês</p></div>
    </div>
  );
}

export function PlanFormDialog({ open, onOpenChange, plan, modules, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(plan ? { ...EMPTY, ...plan, module_prices: { ...(plan.module_prices || {}) }, featuresText: (plan.features || []).join("\n") } : { ...EMPTY, featuresText: "" }); }, [open, plan]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const toggleMod = (m) => setF((x) => ({ ...x, modules: x.modules.includes(m) ? x.modules.filter((y) => y !== m) : [...x.modules, m] }));
  const setPrice = (m, v) => setF((x) => ({ ...x, module_prices: { ...x.module_prices, [m]: v } }));
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...f, features: (f.featuresText || "").split("\n"), module_prices: Object.fromEntries(Object.entries(f.module_prices).map(([k, v]) => [k, cents(v) / 100])) };
      NUMS.forEach((k) => { body[k] = Number(f[k]) || 0; });
      ["featuresText", "tenants", "id", "created_at", "price", "price_yearly"].forEach((k) => delete body[k]);
      if (plan) await adminApi.put(`/plans/${plan.id}`, body); else await adminApi.post("/plans", body);
      toast.success("Plano salvo");
      onSaved(); onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="plan-form-dialog">
        <DialogHeader><DialogTitle>{plan ? `Editar plano ${plan.name}` : "Novo plano"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Nome do plano"><Input value={f.name} onChange={set("name")} data-testid="plan-name-input" /></Field>
          <Field label="Descrição curta"><Input value={f.description} onChange={set("description")} data-testid="plan-description-input" /></Field>
          <Field label="Limite de usuários" hint="0 = ilimitado"><Input type="number" value={f.max_users} onChange={set("max_users")} data-testid="plan-max-users-input" /></Field>
          <Field label="Limite de produtos" hint="0 = ilimitado"><Input type="number" value={f.max_products} onChange={set("max_products")} data-testid="plan-max-products-input" /></Field>
          <Field label="Limite de pedidos por mês" hint="0 = ilimitado"><Input type="number" value={f.max_orders} onChange={set("max_orders")} data-testid="plan-max-orders-input" /></Field>
          <Field label="Quantidade de lojas" hint="0 = ilimitado"><Input type="number" value={f.max_stores} onChange={set("max_stores")} data-testid="plan-max-stores-input" /></Field>
          <Field label="Suporte incluso"><Input value={f.support} onChange={set("support")} data-testid="plan-support-input" /></Field>
          <Field label="Ordem de exibição"><Input type="number" value={f.order} onChange={set("order")} /></Field>
          <label className="sm:col-span-2 flex items-start gap-3 rounded-lg border border-dashed border-primary/50 bg-primary/5 p-3 text-sm">
            <Switch checked={!!f.custom} onCheckedChange={(v) => setF((x) => ({ ...x, custom: v }))} className="mt-0.5" data-testid="plan-custom-switch" />
            <span><b>Personalize seu plano</b><span className="block text-xs text-muted-foreground">Ligado, o cliente monta o próprio plano escolhendo entre os módulos ligados abaixo, no site e na tela Planos.</span></span>
          </label>
          <ModulePrices f={f} modules={modules} toggleMod={toggleMod} setPrice={setPrice} />
          <PriceSummary f={f} set={set} />
          <Field label="Destaques extras (um por linha)" className="sm:col-span-2"><Textarea rows={3} value={f.featuresText} onChange={set("featuresText")} data-testid="plan-features-input" /></Field>
          <label className="flex items-center gap-3 text-sm"><Switch checked={f.status === "active"} onCheckedChange={(v) => setF((x) => ({ ...x, status: v ? "active" : "inactive" }))} data-testid="plan-status-switch" />Plano ativo (visível para venda)</label>
          <label className="flex items-center gap-3 text-sm"><Switch checked={f.highlight} onCheckedChange={(v) => setF((x) => ({ ...x, highlight: v }))} data-testid="plan-highlight-switch" />Destacar como "Mais escolhido"</label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={busy} data-testid="plan-save-btn">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Salvar plano</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
